import { CreditCard, ScrollText, ShieldCheck, Users } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { hasAnyAdminAccess } from '../admin/adminAccess'
import { useConsent } from '../../consent/useConsent'
import { useSettingsModal } from '../settings/settings-modal/SettingsModalProvider'
import type { SettingsSectionId } from '../settings/settings-modal/settingsSections'
import { useBillingVisible } from '../../features/billing/useBillingVisible'
import { useSparks } from '../../api/hooks'
import { useLocaleFormat } from '../../i18n/useLocaleFormat'
import { useTeamsEnabled } from '../../features/teams/useTeamsEnabled'
import UserMenuTeamSection from '../teams/UserMenuTeamSection'
import UserMenuActiveTeamLabel from '../teams/UserMenuActiveTeamLabel'
import { createDebug, createNadDebug } from '../../utils/debugLog'
import BrandMark from './BrandMark'
import UserMenu, { UserMenuItem } from './UserMenu'
import { HEADER_ACTIONS_GAP, HEADER_PADDING_X, HEADER_ROW } from './headerGeometry'

const log = createDebug('dw:account-self-service:header')
const accountSettingsLog = createNadDebug('dw:account-settings-modal')

export default function Header() {
  const navigate = useNavigate()
  const { t } = useTranslation('header')
  const { user, isAuthenticated, signOut } = useAuth()
  const { openSettings: openCookieSettings } = useConsent()
  // One shared dialog, owned above <Routes>. Each menu item names the section
  // it should land on, which is what makes "click any settings entry, get the
  // modal with that entry selected" true without per-item modal state here.
  const { open: openSettingsModal } = useSettingsModal()
  // Same predicate the settings-section registry uses, so the menu entry and
  // the section it opens can never disagree about whether billing exists.
  const billingVisible = useBillingVisible()
  // Same predicate the section registry uses, so the menu entry and the section
  // it opens cannot disagree about whether Teams exists.
  const teamsEnabled = useTeamsEnabled()
  // No project id: this header renders on the dashboard and landing, outside any
  // project, so the endpoint falls back to the actor's own wallet — which is the
  // right wallet for an account-level menu. Inside a project the canvas badge
  // (`SparksBalance`) names the project and can legitimately show a different
  // one. The query is shared with every other `useSparks()` caller, so this adds
  // no extra request.
  const { formatNumber } = useLocaleFormat()
  const { data: sparksData } = useSparks()
  const sparks = sparksData?.pool ?? sparksData?.balance

  // Typed from the section registry rather than a hand-written union — a new
  // section should not need this call site edited to stay correct.
  const openSection = (section: SettingsSectionId) => {
    log('open-settings-modal section=%s', section)
    accountSettingsLog({
      event: 'menu-settings-click',
      trace_id: 'account-settings-modal:dashboard',
      surface: 'dashboard',
      entry_point: 'header-user-menu',
      section,
    })
    openSettingsModal(section)
  }

  // Sign-out from the dropdown. The settings dialog has its own Log out row,
  // which additionally clears the active section's dirty guard first.
  const signOutAndGoHome = async () => {
    await signOut()
    navigate('/')
  }

  // Transparent, border-less shell: the shared HEADER_ROW pill brings its own
  // translucent background and blur, so this reads exactly like the landing's —
  // floating over content rather than a docked slab. `pb` mirrors the pill's top
  // inset so the floating band is balanced.
  //
  // In normal flow, NOT sticky — it scrolls away with the page just like the
  // landing nav. It carried `sticky top-[var(--banner-height)]` from before the
  // pill; that is also why the banner offset is gone, since body already applies
  // padding-top for the banner and only a positioned element needed to re-add it.
  // `relative` is retained purely so z-50 still layers the user-menu dropdown
  // above page content.
  return (
    <header className="relative z-50 pb-3 md:pb-4">
      {/* Shell geometry (padding wrapper + centered row) is the shared
          headerGeometry.ts SSOT, so this dashboard header matches the landing
          NavbarV2 exactly — no logo/height/padding jump on the tab transition
          between "/" and "/dashboard" (Workplace post 1573952564362041). */}
      <div className={HEADER_PADDING_X}>
        <div className={HEADER_ROW}>
        <div className="flex items-center gap-3 sm:gap-4">
          <BrandMark surface="app" />
        </div>

        <div className={`flex items-center ${HEADER_ACTIONS_GAP}`}>
          {!isAuthenticated ? (
            <Link
              to="/login"
              className="rounded-[40px] bg-white px-4 py-2.5 ml-1 text-sm font-semibold text-black no-underline transition-colors hover:bg-white/90 cursor-pointer md:px-6 md:py-3"
            >
              {t('signIn')}
            </Link>
          ) : user && (
            <>
              {/* The team selector now lives INSIDE the profile dropdown
                  (UserMenuTeamSection): the standalone pill hid itself
                  whenever you had a single team, which is every account until
                  someone is invited somewhere. */}
              <div className="ml-1">
                <UserMenu
                user={user}
                testIdPrefix="header"
                accountTitle={t('account')}
                avatarAlt={t('avatar')}
                userFallbackLabel={t('user')}
                leading={teamsEnabled ? <UserMenuActiveTeamLabel /> : null}
              >
                {(close) => (
                  <>
                    <UserMenuTeamSection closeMenu={close} />
                    <UserMenuItem
                      testId="header-profile-button"
                      onClick={() => {
                        close()
                        openSection('profile')
                      }}
                      icon={
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      }
                    >
                      {t('profile')}
                    </UserMenuItem>
                    <UserMenuItem
                      testId="header-settings-button"
                      onClick={() => {
                        close()
                        openSection('general')
                      }}
                      icon={
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      }
                    >
                      {t('settings')}
                    </UserMenuItem>
                    <UserMenuItem
                      testId="header-rules-button"
                      onClick={() => {
                        close()
                        openSection('personalization')
                      }}
                      icon={<ScrollText className="w-4 h-4" />}
                    >
                      {t('myRules')}
                    </UserMenuItem>
                    {teamsEnabled && (
                      <UserMenuItem
                        testId="header-teams-button"
                        onClick={() => {
                          close()
                          openSection('teams')
                        }}
                        icon={<Users className="w-4 h-4" strokeWidth={2} />}
                      >
                        {t('teams')}
                      </UserMenuItem>
                    )}
                    {billingVisible && (
                      <UserMenuItem
                        testId="header-usage-credits-button"
                        onClick={() => {
                          close()
                          openSection('usage-credits')
                        }}
                        icon={<CreditCard className="w-4 h-4" strokeWidth={2} />}
                        // The balance, right here in the menu. Until 110 the
                        // welcome bonus was granted as a side effect of the
                        // first balance READ, and the only surfaces that read it
                        // were inside a project — so a new account that signed
                        // up and opened this menu saw no number at all, and had
                        // to enter a canvas before its credits existed. The
                        // grant moved to signup; this makes the result visible
                        // without opening the modal behind it.
                        //
                        // THE POOL, not `balance`: `balance` is
                        // min(pool, allowance), so for a member with a per-member
                        // cap it renders their own quota and reads as the TEAM
                        // being nearly out. Same choice, and the same reasoning,
                        // as SparksBalance.
                        trailing={
                          sparks === undefined ? null : (
                            <span
                              data-testid="header-usage-credits-balance"
                              className="tabular-nums text-white/80"
                            >
                              {formatNumber(sparks)}
                            </span>
                          )
                        }
                      >
                        {t('usageAndCredits')}
                      </UserMenuItem>
                    )}
                    <UserMenuItem
                      testId="header-cookie-settings-button"
                      onClick={() => {
                        close()
                        openCookieSettings()
                      }}
                      icon={
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2a10 10 0 100 20 10 10 0 000-20z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.5 9h.01M14 8h.01M15 13h.01M9.5 14h.01M12 11h.01" />
                        </svg>
                      }
                    >
                      {t('cookieSettings')}
                    </UserMenuItem>
                    {hasAnyAdminAccess(user) && (
                      <UserMenuItem
                        testId="header-admin-console-button"
                        onClick={() => {
                          close()
                          navigate('/admin')
                        }}
                        icon={<ShieldCheck className="h-4 w-4" strokeWidth={2} />}
                      >
                        {t('adminConsole')}
                      </UserMenuItem>
                    )}
                    <UserMenuItem
                      onClick={() => {
                        close()
                        void signOutAndGoHome()
                      }}
                      icon={
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                      }
                    >
                      {t('signOut')}
                    </UserMenuItem>
                  </>
                )}
                </UserMenu>
              </div>
            </>
          )}
        </div>
        </div>
      </div>

    </header>
  )
}
